/**
 * Field validation helpers for ChoiceFlow
 */

/**
 * Validate text field
 * @param {object} fieldDef - Field definition
 * @param {string} value - Field value
 * @returns {string|null} Error message or null if valid
 */
const validateTextField = (fieldDef, value) => {
  const stringValue = String(value || '');

  // Check max length
  if (fieldDef.validate?.maxLength && stringValue.length > fieldDef.validate.maxLength) {
    return `Maximum ${fieldDef.validate.maxLength} characters allowed`;
  }

  // Check regex pattern
  if (fieldDef.validate?.pattern && stringValue) {
    try {
      const regex = new RegExp(fieldDef.validate.pattern);
      if (!regex.test(stringValue)) {
        return `Please enter a valid ${fieldDef.label?.toLowerCase() || 'value'}`;
      }
    } catch (e) {
      console.warn('[ChoiceFlow] Invalid regex pattern:', fieldDef.validate.pattern);
    }
  }

  return null;
};

/**
 * Validate number field
 * @param {object} fieldDef - Field definition
 * @param {number} value - Field value
 * @returns {string|null} Error message or null if valid
 */
const validateNumberField = (fieldDef, value) => {
  const numValue = Number(value);

  // Check if it's a valid number
  if (Number.isNaN(numValue)) {
    return 'Please enter a valid number';
  }

  // Check min value
  if (fieldDef.validate?.min !== undefined && numValue < fieldDef.validate.min) {
    return `Minimum value is ${fieldDef.validate.min}`;
  }

  // Check max value
  if (fieldDef.validate?.max !== undefined && numValue > fieldDef.validate.max) {
    return `Maximum value is ${fieldDef.validate.max}`;
  }

  return null;
};

/**
 * Validate range field
 * @param {object} fieldDef - Field definition
 * @param {number} value - Field value
 * @returns {string|null} Error message or null if valid
 */
const validateRangeField = (fieldDef, value) => {
  const numValue = Number(value);

  // Check if it's a valid number
  if (Number.isNaN(numValue)) {
    return 'Please select a valid value';
  }

  // Check bounds (should be enforced by slider, but good to double-check)
  if (numValue < fieldDef.min) {
    return `Value must be at least ${fieldDef.min}`;
  }

  if (numValue > fieldDef.max) {
    return `Value must be at most ${fieldDef.max}`;
  }

  return null;
};

/**
 * Validate multi-select field
 * @param {object} fieldDef - Field definition
 * @param {Array} value - Field value (array of selected option IDs)
 * @returns {string|null} Error message or null if valid
 */
const validateMultiSelectField = (fieldDef, value) => {
  const selectedValues = Array.isArray(value) ? value : [];

  // Check required (at least one selection)
  if (fieldDef.required && selectedValues.length === 0) {
    return `Please select at least one ${fieldDef.label?.toLowerCase() || 'option'}`;
  }

  // Check max selections
  if (fieldDef.maxSelected && selectedValues.length > fieldDef.maxSelected) {
    return `Please select at most ${fieldDef.maxSelected} option${fieldDef.maxSelected === 1 ? '' : 's'}`;
  }

  // Check that all selected values are valid options
  const validOptionIds = fieldDef.options?.map((opt) => opt.id) || [];
  const invalidSelections = selectedValues.filter(
    (selection) => selection?.selectedId && !validOptionIds.includes(selection.selectedId)
  );
  if (invalidSelections.length > 0) {
    return 'Some selected options are invalid';
  }

  return null;
};

/**
 * Validate single-select field
 * @param {object} fieldDef - Field definition
 * @param {string} value - Field value (selected option ID)
 * @returns {string|null} Error message or null if valid
 */
const validateSingleSelectField = (fieldDef, value) => {
  // Handle both string and object value types
  const selectedId = typeof value === 'string' ? value : value?.selectedId;

  // Check that selected value is a valid option
  if (selectedId) {
    const validOptionIds = fieldDef.options?.map((opt) => opt.id) || [];
    if (!validOptionIds.includes(selectedId)) {
      return 'Selected option is invalid';
    }

    // If this is an input-type option, validate the input value
    const selectedOption = fieldDef.options?.find((opt) => opt.id === selectedId);
    if (selectedOption?.type === 'input') {
      // Value must be an object with inputValue for input-type options
      // if (typeof value !== 'object' || !value.inputValue) {
      //   return 'Please provide additional details';
      // }

      // Validate the input value using text field validation
      const inputError = validateTextField(
        {
          ...fieldDef,
          label: selectedOption.label
        },
        value.inputValue
      );
      if (inputError) return inputError;
    }
  }

  return null;
};

/**
 * Validate checkbox field
 * @param {object} fieldDef - Field definition
 * @param {boolean} value - Field value
 * @returns {string|null} Error message or null if valid
 */
const validateCheckboxField = (fieldDef, value) => {
  // For required checkboxes, they must be checked (true)
  if (fieldDef.required && !value) {
    return `You must agree to ${fieldDef.label?.toLowerCase() || 'this'}`;
  }

  return null;
};

/**
 * Validate a single field based on its definition and current value
 * @param {object} fieldDef - Field definition
 * @param {any} value - Field value to validate
 * @returns {string|null} Error message or null if valid
 */
export const validateField = (fieldDef, value) => {
  if (!fieldDef) {
    return null;
  }

  // // Check required fields
  if (fieldDef.required && (value === null || value === undefined || value === '')) {
    return `${fieldDef.label || 'This field'} is required`;
  }

  // Skip validation if field is empty and not required
  if (!fieldDef.required && (value === null || value === undefined || value === '')) {
    return null;
  }

  // Validate based on field type
  switch (fieldDef.type) {
    case 'text':
      return validateTextField(fieldDef, value);
    case 'input':
      return validateTextField(fieldDef, value); // Same validation as text
    case 'number':
      return validateNumberField(fieldDef, value);
    case 'range':
      return validateRangeField(fieldDef, value);
    case 'multi_select':
      return validateMultiSelectField(fieldDef, value);
    case 'single_select':
      return validateSingleSelectField(fieldDef, value);
    case 'checkbox':
      return validateCheckboxField(fieldDef, value);
    default:
      return null;
  }
};

/**
 * Validate all visible fields on a page
 * @param {Array} fields - Array of field definitions
 * @param {object} answers - Current form answers
 * @param {Function} isFieldVisible - Function to check if field is visible
 * @returns {object} Object with field IDs as keys and error messages as values
 */
export const validatePage = (fields, answers, isFieldVisible) => {
  const errors = {};

  if (!Array.isArray(fields)) {
    return errors;
  }

  fields.forEach((field) => {
    // Only validate visible fields
    if (isFieldVisible(field)) {
      const error = validateField(field, answers[field.id]);
      if (error) {
        errors[field.id] = error;
      }
    }
  });

  return errors;
};

/**
 * Check if a page has any validation errors
 * @param {Array} fields - Array of field definitions
 * @param {object} answers - Current form answers
 * @param {Function} isFieldVisible - Function to check if field is visible
 * @returns {boolean} True if page is valid (no errors)
 */
export const isPageValid = (fields, answers, isFieldVisible) => {
  const errors = validatePage(fields, answers, isFieldVisible);
  return Object.keys(errors).length === 0;
};

/**
 * Get error message to display for a field (excludes required field errors)
 * This is separate from validateField to allow hiding required field error messages
 * while still using them for form validation (button state)
 * @param {object} fieldDef - Field definition
 * @param {any} value - Field value to validate
 * @returns {string|null} Error message to display or null if no message should be shown
 */
export const getFieldErrorMessage = (fieldDef, value) => {
  if (!fieldDef) {
    return null;
  }

  // Skip required field error messages - these are handled via disabled next button
  if (fieldDef.required && (value === null || value === undefined || value === '')) {
    return null;
  }

  // Skip validation if field is empty and not required
  if (!fieldDef.required && (value === null || value === undefined || value === '')) {
    return null;
  }

  // Show all other validation errors (pattern, length, etc.)
  // Use the same validation logic as validateField but skip the required check
  switch (fieldDef.type) {
    case 'text':
      return validateTextField(fieldDef, value);
    case 'input':
      return validateTextField(fieldDef, value); // Same validation as text
    case 'number':
      return validateNumberField(fieldDef, value);
    case 'range':
      return validateRangeField(fieldDef, value);
    case 'multi_select':
      return validateMultiSelectField(fieldDef, value);
    case 'single_select':
      return validateSingleSelectField(fieldDef, value);
    case 'checkbox':
      return validateCheckboxField(fieldDef, value);
    default:
      return null;
  }
};

/**
 * Get all required fields that are missing values
 * @param {Array} fields - Array of field definitions
 * @param {object} answers - Current form answers
 * @param {Function} isFieldVisible - Function to check if field is visible
 * @returns {Array} Array of field IDs that are required but missing values
 */
export const getMissingRequiredFields = (fields, answers, isFieldVisible) => {
  if (!Array.isArray(fields)) {
    return [];
  }

  return fields
    .filter((field) => isFieldVisible(field) && field.required)
    .filter((field) => {
      const value = answers[field.id];
      return (
        value === null ||
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0)
      );
    })
    .map((field) => field.id);
};
